import { useState } from 'react';
import { type MoodEntry, formatDate, saveEntry } from '../api/mockData';
import { Calendar } from './Calendar';
import { DayDetail } from './DayDetail';

interface CalendarViewProps {
  entries: Record<string, MoodEntry>;
  onEntriesChange: (entries: Record<string, MoodEntry>) => void;
}

export function CalendarView({ entries, onEntriesChange }: CalendarViewProps) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const handlePrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const handleSaveEntry = (entry: MoodEntry) => {
    const updated = saveEntry(entry);
    onEntriesChange(updated);
    setSelectedDate(null);
  };

  // Mood summary for this month
  const monthEntries = Object.values(entries).filter(e => {
    const d = new Date(e.date + 'T12:00:00');
    return d.getFullYear() === year && d.getMonth() === month;
  });

  const today = formatDate(new Date());
  const streakCount = calculateStreak(entries, today);

  return (
    <div style={{
      padding: '24px 20px',
      maxWidth: '480px',
      margin: '0 auto',
    }}>
      {/* Stats bar */}
      <div className="animate-fade-up" style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '24px',
      }}>
        <div style={{
          flex: 1,
          padding: '14px 12px',
          borderRadius: '14px',
          background: 'white',
          border: '1px solid var(--journal-line)',
          textAlign: 'center',
        }}>
          <p style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: 800,
            color: 'var(--journal-accent)',
            margin: 0,
            lineHeight: 1,
          }}>
            {monthEntries.length}
          </p>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.65rem',
            color: 'var(--journal-ink-faint)',
            margin: '4px 0 0',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 500,
          }}>
            This month
          </p>
        </div>
        <div style={{
          flex: 1,
          padding: '14px 12px',
          borderRadius: '14px',
          background: 'white',
          border: '1px solid var(--journal-line)',
          textAlign: 'center',
        }}>
          <p style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: 800,
            color: 'var(--mood-great-deep)',
            margin: 0,
            lineHeight: 1,
          }}>
            {streakCount}
          </p>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.65rem',
            color: 'var(--journal-ink-faint)',
            margin: '4px 0 0',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 500,
          }}>
            Day streak
          </p>
        </div>
        <div style={{
          flex: 1,
          padding: '14px 12px',
          borderRadius: '14px',
          background: 'white',
          border: '1px solid var(--journal-line)',
          textAlign: 'center',
        }}>
          <p style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: 800,
            color: 'var(--journal-ink)',
            margin: 0,
            lineHeight: 1,
          }}>
            {Object.keys(entries).length}
          </p>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.65rem',
            color: 'var(--journal-ink-faint)',
            margin: '4px 0 0',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 500,
          }}>
            Total entries
          </p>
        </div>
      </div>

      {/* Calendar */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '20px 16px',
        border: '1px solid var(--journal-line)',
        boxShadow: '0 2px 12px rgba(58, 46, 40, 0.04)',
      }}>
        <Calendar
          year={year}
          month={month}
          entries={entries}
          selectedDate={selectedDate || undefined}
          onSelectDate={setSelectedDate}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
        />
      </div>

      {/* Mood legend */}
      <div className="animate-fade-up stagger-3" style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '20px',
        flexWrap: 'wrap',
      }}>
        {(['great', 'good', 'okay', 'bad', 'awful'] as const).map(level => {
          const config = { great: { emoji: '😄', color: 'var(--mood-great)' }, good: { emoji: '🙂', color: 'var(--mood-good)' }, okay: { emoji: '😐', color: 'var(--mood-okay)' }, bad: { emoji: '😟', color: 'var(--mood-bad)' }, awful: { emoji: '😢', color: 'var(--mood-awful)' } }[level];
          return (
            <div key={level} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <span style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: config.color,
              }} />
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.65rem',
                color: 'var(--journal-ink-faint)',
                textTransform: 'capitalize',
              }}>
                {level}
              </span>
            </div>
          );
        })}
      </div>

      {/* Day detail modal */}
      {selectedDate && (
        <DayDetail
          date={selectedDate}
          entry={entries[selectedDate]}
          onSave={handleSaveEntry}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </div>
  );
}

function calculateStreak(entries: Record<string, MoodEntry>, today: string): number {
  let streak = 0;
  const current = new Date(today + 'T12:00:00');

  while (true) {
    const dateStr = formatDate(current);
    if (entries[dateStr]) {
      streak++;
      current.setDate(current.getDate() - 1);
    } else {
      break;
    }
  }

  return streak;
}
